import { logger } from "../../utils/logger";
import type { BodyUUID } from "../../types/scene";
import type { PickPlaceStage } from "./pickPlaceServoConfig";
import type { RankedGraspWorldZ } from "./graspRanking";
import type { IkDebugWindow } from "./types";

export function isPickplaceDebugEnabled(): boolean {
  try {
    return (window as IkDebugWindow).__ikPickplaceDebug === true;
  } catch {
    return false;
  }
}

export function logServoConfig(args: {
  useGripperTargetSite: boolean;
  targetBodyUuid: BodyUUID;
  endEffectorUuid: BodyUUID;
  dropoffBodyUuid: BodyUUID | null;
  targetOffset: {
    siteName?: string;
    siteBodyUuid?: BodyUUID;
    offsetZFrame: { x: number; y: number; z: number };
  } | null;
}) {
  if (!isPickplaceDebugEnabled()) return;
  const { useGripperTargetSite, targetBodyUuid, endEffectorUuid, dropoffBodyUuid, targetOffset } =
    args;
  logger.info("[ik-pickplace] SERVO_CONFIG", {
    useGripperTargetSite,
    targetBodyUuid,
    endEffectorUuid,
    dropoffBodyUuid,
    targetOffset: targetOffset
      ? {
          siteName: targetOffset.siteName,
          siteBodyUuid: targetOffset.siteBodyUuid,
          offsetZFrame: {
            x: Number(targetOffset.offsetZFrame.x.toFixed(4)),
            y: Number(targetOffset.offsetZFrame.y.toFixed(4)),
            z: Number(targetOffset.offsetZFrame.z.toFixed(4)),
          },
        }
      : null,
  });
}

export function logSelectedGrasp(args: {
  targetBodyUuid: BodyUUID;
  stage: PickPlaceStage;
  selectedGraspWorldZ: RankedGraspWorldZ;
  graspCandidateOffset: number;
  candidateCount: number;
  locked: boolean;
  selectedGraspLoggedKey: string | null;
}): string | null {
  const {
    targetBodyUuid,
    stage,
    selectedGraspWorldZ,
    graspCandidateOffset,
    candidateCount,
    locked,
    selectedGraspLoggedKey,
  } = args;
  const selectedKey = [
    String(targetBodyUuid || ""),
    String(stage || ""),
    String(selectedGraspWorldZ.sourceIndex),
    String(graspCandidateOffset),
    locked ? "locked" : "free",
  ].join("|");
  if (selectedGraspLoggedKey === selectedKey) return selectedGraspLoggedKey;

  logger.info("[ik-pickplace] GRASP_SELECTED", {
    targetBodyUuid,
    stage,
    sourceIndex: selectedGraspWorldZ.sourceIndex,
    combinedScore: Number(selectedGraspWorldZ.combinedScore.toFixed(4)),
    topDownScore: Number(selectedGraspWorldZ.topDownScore.toFixed(4)),
    distanceScore: Number(selectedGraspWorldZ.distanceScore.toFixed(4)),
    candidateCount,
    graspCandidateOffset,
    locked,
  });
  try {
    window.dispatchEvent(
      new CustomEvent("ik-selected-grasp", {
        detail: {
          targetBodyUuid,
          sourceIndex: selectedGraspWorldZ.sourceIndex,
          stage,
          candidateCount,
          combinedScore: selectedGraspWorldZ.combinedScore,
          topDownScore: selectedGraspWorldZ.topDownScore,
          distanceScore: selectedGraspWorldZ.distanceScore,
        },
      })
    );
  } catch {
    // ignore
  }
  return selectedKey;
}
